import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Deposit.css';

const Deposit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [gateway, setGateway] = useState('chapa');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [recentDeposits, setRecentDeposits] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }
    setUser(JSON.parse(userData));
    fetchRecentDeposits();

    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status === 'success') {
      setSuccessMessage('✅ Payment successful! Balance updated.');
    } else if (status === 'failed') {
      setErrorMessage('❌ Payment failed. Please try again.');
    }
  }, [navigate, location]);

  const fetchRecentDeposits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/deposits/recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentDeposits(response.data || []);
    } catch (error) {
      console.error('Error fetching deposits:', error);
    }
  };

  const handlePayment = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 50) {
      setErrorMessage('Minimum deposit amount is ETB 50');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/chapa/initialize`,
        { amount: amt },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setErrorMessage(response.data.message || 'Failed to initialize payment');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage(error.response?.data?.message || 'Payment initialization failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', text: '⏳ Pending' },
      approved: { class: 'status-approved', text: '✅ Approved' },
      rejected: { class: 'status-rejected', text: '❌ Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  return (
    <div className="deposit-container">
      <div className="deposit-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>← Back</button>
          <h1>💰 Deposit Funds</h1>
        </div>
        <div className="header-right">
          <span className="balance-display">
            Balance: <strong>ETB {user?.wallet?.balance?.toFixed(2) || '0.00'}</strong>
          </span>
        </div>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <div className="deposit-content">
        <div className="step-section">
          <div className="step-header">
            <span className="step-number">1</span>
            <h3>Enter Amount</h3>
          </div>
          <div className="form-group">
            <label>Amount (ETB) *</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Min. ETB 50"
              min="50"
              step="1"
              style={{ padding: '15px', fontSize: '18px', border: '2px solid #ddd', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
            />
            <small>Minimum deposit: ETB 50</small>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
            {[50, 100, 200, 500, 1000].map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(amt.toString())}
                style={{
                  padding: '10px 20px', borderRadius: '25px', border: '2px solid #2e7d32',
                  background: amount === amt.toString() ? '#2e7d32' : '#fff',
                  color: amount === amt.toString() ? '#fff' : '#2e7d32',
                  cursor: 'pointer', fontWeight: 'bold', fontSize: '14px'
                }}
              >
                ETB {amt}
              </button>
            ))}
          </div>
        </div>

        <div className="step-section">
          <div className="step-header">
            <span className="step-number">2</span>
            <h3>Select Payment Method</h3>
          </div>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div
              onClick={() => setGateway('chapa')}
              style={{
                flex: 1, minWidth: '200px', padding: '20px', borderRadius: '12px',
                border: '3px solid', borderColor: gateway === 'chapa' ? '#0d47a1' : '#e0e0e0',
                background: gateway === 'chapa' ? '#e3f2fd' : '#fff', cursor: 'pointer',
                textAlign: 'center', transition: 'all 0.2s'
              }}
            >
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>⚡</div>
              <h4 style={{ margin: '0 0 5px 0', color: '#0d47a1' }}>Chapa</h4>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Telebirr, CBE Birr, M-Pesa, Cards</p>
            </div>
            <div
              onClick={() => setGateway('arifpay')}
              style={{
                flex: 1, minWidth: '200px', padding: '20px', borderRadius: '12px',
                border: '3px solid', borderColor: gateway === 'arifpay' ? '#e65100' : '#e0e0e0',
                background: gateway === 'arifpay' ? '#fff3e0' : '#fff', cursor: 'pointer',
                textAlign: 'center', transition: 'all 0.2s'
              }}
            >
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>🏦</div>
              <h4 style={{ margin: '0 0 5px 0', color: '#e65100' }}>ArifPay</h4>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Telebirr, CBE Birr, M-Pesa, Visa</p>
            </div>
          </div>
        </div>

        <div className="step-section">
          <button
            onClick={handlePayment}
            disabled={loading}
            style={{
              width: '100%', padding: '18px',
              background: loading ? '#9e9e9e' : 'linear-gradient(135deg, #2e7d32, #43a047)',
              color: '#fff', border: 'none', borderRadius: '10px',
              fontSize: '18px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '⏳ Redirecting...' : `💳 Pay ETB ${amount || '0'}`}
          </button>
        </div>
      </div>

      {recentDeposits.length > 0 && (
        <div className="recent-deposits">
          <div className="recent-header">
            <h3>📊 Recent Deposits</h3>
            <span className="recent-count">{recentDeposits.length} requests</span>
          </div>
          <div className="deposits-table-wrapper">
            <table>
              <thead>
                <tr><th>Date</th><th>Amount</th><th>Method</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentDeposits.map((deposit) => (
                  <tr key={deposit._id}>
                    <td>{formatDate(deposit.createdAt)}</td>
                    <td><strong>ETB {deposit.amount}</strong></td>
                    <td>{deposit.paymentMethod}</td>
                    <td>{getStatusBadge(deposit.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;